/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import uniq from 'lodash.uniq';
import {BulkActionEntity, GenericObject} from '@ion/core/src/types/entities';
import {isValidESN} from '@ion/core/src/utils/DeviceUtils';
import {FormState} from '@ion/dls/src/hooks/useFormState';

export enum BulkSelectEntitiesFormFields {
  EntityIdentifierType = 'entityIdentifierType',
  EntityIdentifiers = 'entityIdentifiers',
  File = 'file'
}

export const MAXIMUM_ENTITY_COUNT = 5000;

// Builds the initial form state for the Bulk entity Action dialog.
export const getInitialBulkSelectEntitiesFormState = (): FormState => ({
  [BulkSelectEntitiesFormFields.EntityIdentifierType] : {
    name     : BulkSelectEntitiesFormFields.EntityIdentifierType,
    required : true
  },
  [BulkSelectEntitiesFormFields.EntityIdentifiers] : {
    name     : BulkSelectEntitiesFormFields.EntityIdentifiers,
    required : true
  },
  [BulkSelectEntitiesFormFields.File] : {
    name     : BulkSelectEntitiesFormFields.File,
    required : false,
    value    : {
      importParams : {
        items      : [],
        isComplete : false,
        isFailure  : false,
        workbook   : null
      },
      importComplete : false,
      importStatus   : false
    }
  }
});

// Serializes the form state into an API-consumable entity Config
export const serialize = (
  formState: FormState): GenericObject => {
  const serialized: GenericObject = {};
  for (const [key, {value}] of Object.entries(formState)) {
    if (value) {
      serialized[key] = value;
    }
  }
  return serialized;
};

/**
 * Determines if the given import params are for a valid file
 * @param importParams import details from uploading / importing a file
 * @returns true if import params contain a file, false otherwise
 */
export const isValidFile = (importParams) => importParams?.fileName ? importParams : false;

/**
 * Determines if the given ids are valid based on the provided entity type (device, asset, etc) & identifier type.
 * @param ids list of identifiers to validate
 * @param entity device, asset, etc
 * @returns true if valid, false otherwise
 */
export const arePastedIdsValid = (ids, entity: BulkActionEntity, validate = true) => {
  if (ids?.length) {
    if (entity === BulkActionEntity.DEVICES) {
      return validate ? !ids.some(id => !isValidESN(id)) : true;
    } else {
      return true;
    }
  }
  return true;
};

/**
 * Parses the specified string containing pasted identifiers (delimiters can be
 * whitespace or commas) and returns a list of the identifiers with any duplicates
 * removed. Note that any leading and trailing delimiters are removed during the
 * parsing process.
 * @param ids string containing identifiers to parse
 * @returns list of identifiers
 */
export const parsePastedIds = (ids: string): string[] => {
  if (ids?.length) {
    return uniq(ids.replace(/^[\s,]+|[\s,]+$/gm, '').split(/[\s,]+/));
  }
  return [];
};
